/**
 * ContainerFillControl — the Container block's Background control, standardised
 * to the app-standard fill control (GORDON 2026-09-15, Container block settings
 * pane standardisation, item 3): "Background must use the app standard fill
 * control: Colors / Gradient / Patterns / Image tabs, brand swatches, Media
 * Library for image selection, NO Opacity slider." Mirrors the Accordion block's
 * Title Background control (AccordionHeaderBackgroundControl) byte-for-byte in
 * structure/tab set, so Container and Accordion read as ONE design language.
 *
 * Replaces the old ./ContainerBackgroundControl (still used, UNCHANGED, by the
 * unrelated Title Bar block — see containerFillStandard.js's header comment for
 * why this is a separate file rather than a rewrite of that shared one) whose
 * Colors tab carried a bespoke Opacity slider and had no Patterns tab — that was
 * the off-brand control this standardises away, for Container only.
 *
 * REUSE, NOT DUPLICATION — every sub-surface is the app's existing one, the SAME
 * ones AccordionHeaderBackgroundControl and SegmentFillPanel mount:
 *   • Colors   → the existing <ColorPopup> (Preset / Custom / Wheel Palette /
 *                Segment Palette BRAND SWATCHES) — the SAME control Border / Drop
 *                Shadow use on this pane, and Accordion's Border/Content
 *                Background use — with showSlider={false} so there is NO Opacity
 *                slider.
 *   • Gradient → the app's REAL gradient builder <GradientBuilderPanel>.
 *   • Patterns → the existing <BackgroundPatternPanel> (tinted SVG motifs) — the
 *                SAME picker Accordion's Title Background Patterns tab uses.
 *   • Image    → the universal image picker <MediaSelection isGifAllowed> (Upload
 *                / Embed URL / Browse / Recent — the Media Library every image
 *                surface uses).
 *
 * The gradient + pattern + media pickers are lazy-loaded (heavy, browser-only
 * subtrees), mirroring AccordionHeaderBackgroundControl / SegmentFillPanel.
 *
 * Controlled + presentational: reads the ContainerEditor `value` (background /
 * containerFill) and emits partial patches via `onPatch` (== the editor's
 * patch()). It owns no server state; the chosen fill persists as the
 * `containerFill` craft node prop through the container's existing save path —
 * the SAME prop + the SAME containerBackground.js resolve/normalize pipeline the
 * renderer already uses (it already understood a `{type:"pattern",css}` fill).
 */

import React, { useMemo } from "react";
import { tokens } from "../../../../theme/tokens";
import ColorPopup from "../../Shared/ColorPopup";
import {
  STANDARD_FILL_TYPES,
  STANDARD_FILL_TYPE_LABEL,
  defaultGradientFill,
  defaultPatternFill,
  defaultImageFill,
  resolveContainerBackground,
} from "./containerFillStandard";

// Lazy — heavy, browser-only picker subtrees (see AccordionHeaderBackgroundControl
// / SegmentFillPanel precedent).
const GradientBuilderPanel = React.lazy(() =>
  import("../../Shared/SegmentStyling/GradientBuilderPanel")
);
const MediaSelection = React.lazy(() => import("../../Shared/MediaSelection"));
const BackgroundPatternPanel = React.lazy(() =>
  import("../../../backgroundPatterns").then((m) => ({
    default: m.BackgroundPatternPanel,
  }))
);

const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";
const PL = "#9B5AF8";

const S = {
  wrap: { fontFamily: tokens.fontFamily, color: INK },
  label: {
    fontFamily: tokens.fontFamily,
    fontSize: 12,
    fontWeight: tokens.fontWeight.semibold,
    color: INK,
    display: "block",
    marginBottom: 6,
  },
  tablist: { display: "flex", gap: tokens.spacing.xs, flexWrap: "wrap", marginBottom: tokens.spacing.md },
  tab: (active) => ({
    flex: "0 0 auto",
    border: `1px solid ${active ? PL : LINE}`,
    background: active ? "#F1E9FE" : "#FFFFFF",
    color: active ? "#6f27d6" : SUB,
    borderRadius: tokens.radius.md,
    padding: "10px 14px",
    minHeight: 44,
    font: `600 12.5px ${tokens.fontFamily}`,
    cursor: "pointer",
  }),
  previewWrap: { display: "flex", alignItems: "center", gap: tokens.spacing.md, marginBottom: tokens.spacing.md },
  preview: (bg) => ({
    width: 56,
    height: 56,
    borderRadius: tokens.radius.md,
    border: `1px solid ${LINE}`,
    background: bg,
    flex: "0 0 auto",
  }),
  hint: { fontSize: 11, color: SUB, marginTop: tokens.spacing.sm },
  sub: { marginTop: tokens.spacing.md },
};

const DEFAULT_COLOR = "#F3ECFF";

const extractUrl = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.src || value.url || value.file || value.path || "";
};

const ContainerFillControl = ({ value = {}, onPatch = () => {} }) => {
  const containerFill = value.containerFill;
  // Which TAB is active is driven by the RAW stored `.type`, not fillTypeOf():
  // fillTypeOf()/normalizeContainerFill() intentionally fall back to "color" for
  // an empty/incomplete structured fill (e.g. a just-picked Pattern with no motif
  // chosen yet, or a Gradient in transit) — correct for the RENDERER (paint the
  // legacy solid color until there's real content), but wrong for tab selection
  // (the user picked "Patterns"; the Patterns sub-panel must stay open and
  // reachable so they CAN pick a motif, not silently bounce back to Colors).
  const currentType =
    containerFill && STANDARD_FILL_TYPES.includes(containerFill.type)
      ? containerFill.type
      : "color";
  const background = value.background || DEFAULT_COLOR;

  // The live preview swatch = exactly what the container will paint (legacy
  // solid color for an empty/incomplete structured fill).
  const previewBg = useMemo(() => {
    const res = resolveContainerBackground(containerFill);
    return res.mode === "fill" ? res.css : background;
  }, [containerFill, background]);

  const selectType = (type) => {
    if (type === currentType) return;
    if (type === "color") {
      // Explicit clear (null, not undefined): the wiring deletes the structured
      // fill prop so the renderer falls back to the legacy solid-color path.
      onPatch({ containerFill: null });
    } else if (type === "gradient") {
      onPatch({
        containerFill:
          containerFill && containerFill.type === "gradient" ? containerFill : defaultGradientFill(),
      });
    } else if (type === "pattern") {
      onPatch({
        containerFill:
          containerFill && containerFill.type === "pattern" ? containerFill : defaultPatternFill(),
      });
    } else {
      onPatch({
        containerFill:
          containerFill && containerFill.type === "image" ? containerFill : defaultImageFill(),
      });
    }
  };

  const onGradientChange = (gradient) =>
    onPatch({ containerFill: { type: "gradient", gradient } });

  const applyPattern = (css) => onPatch({ containerFill: { type: "pattern", css: css || "" } });

  const [media, setMedia] = React.useState(null);
  const applyMedia = (_type, val) => {
    const url = extractUrl(val);
    if (!url) return;
    onPatch({
      containerFill: {
        type: "image",
        image: { ...((containerFill && containerFill.image) || {}), url },
      },
    });
  };
  const clearMedia = () => onPatch({ containerFill: { type: "image", image: {} } });

  return (
    <div style={S.wrap} data-container-fill>
      <span style={S.label} id="container-fill-label">
        Background
      </span>

      {/* live preview swatch (what the container paints) */}
      <div style={S.previewWrap}>
        <div
          style={S.preview(previewBg)}
          role="img"
          aria-label={`Container background preview: ${STANDARD_FILL_TYPE_LABEL[currentType]}`}
          data-container-fill-preview
        />
      </div>

      {/* type selector: real ARIA radiogroup, app-standard Colors/Gradient/
          Patterns/Image order (matches Accordion's Title Background). */}
      <div role="radiogroup" aria-labelledby="container-fill-label" style={S.tablist}>
        {STANDARD_FILL_TYPES.map((t) => {
          const active = currentType === t;
          return (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              style={S.tab(active)}
              onClick={() => selectType(t)}
              data-container-fill-type={t}
            >
              {STANDARD_FILL_TYPE_LABEL[t]}
            </button>
          );
        })}
      </div>

      {/* per-type sub-panel */}
      {currentType === "color" && (
        <ColorPopup
          title="Background Fill"
          background={background}
          handleChangeBackgroundColor={(c) => onPatch({ background: c })}
          resetBackgroundColor={() => onPatch({ background: DEFAULT_COLOR })}
          showSlider={false}
          isHideGradient
          showSuggestedColorPalette
        />
      )}

      {currentType === "gradient" && (
        <div style={S.sub} data-subpanel="gradient">
          <React.Suspense fallback={<p style={S.hint}>Loading gradient builder…</p>}>
            <GradientBuilderPanel
              gradient={(containerFill && containerFill.gradient) || undefined}
              onChange={onGradientChange}
            />
          </React.Suspense>
        </div>
      )}

      {currentType === "pattern" && (
        <div style={S.sub} data-subpanel="pattern">
          <p style={S.hint}>Pick a motif; it tints to your palette.</p>
          <React.Suspense fallback={<p style={S.hint}>Loading picker…</p>}>
            <BackgroundPatternPanel
              value={(containerFill && containerFill.css) || ""}
              onApply={applyPattern}
              segmentColor={background}
            />
          </React.Suspense>
        </div>
      )}

      {currentType === "image" && (
        <div style={S.sub} data-subpanel="image">
          <p style={S.hint}>
            Upload or pick an image or GIF; it fills the container (cover).
          </p>
          <React.Suspense fallback={<p style={S.hint}>Loading picker…</p>}>
            <MediaSelection
              type="image"
              isGifAllowed
              categories={["Upload", "Embed URL", "Browse", "Recent"]}
              media={media}
              setMedia={setMedia}
              handleChange={applyMedia}
              handleRemove={clearMedia}
              handleTitle={() => {}}
            />
          </React.Suspense>
        </div>
      )}
    </div>
  );
};

export default ContainerFillControl;
